import { ISimplifiedNode } from '../../components/ingestion/IngestedTree'
import {
  AttributeType,
  IngestionContext,
  NormalizedIngestionSource,
} from './types'
import { camelToTitle } from '../../components/Helpers'
import { createNoteNode, createSkillsList } from './nodeCreation'
import { createUnorderedList } from '../html_utility'

export function attributeIngestion(
  newNode: { nodes: ISimplifiedNode[] },
  attr: AttributeType,
  sourceNode: Partial<NormalizedIngestionSource>,
  context: IngestionContext,
) {
  newNode.nodes.push(...createAttributeNodes(attr, sourceNode, context))
}

export function createAttributeNodes(
  attr: AttributeType,
  sourceNode: Partial<NormalizedIngestionSource>,
  context: IngestionContext,
): ISimplifiedNode[] {
  if (attr.isMetadata) return []
  if (attr.key === 'standards')
    return standardsIngestion(attr, sourceNode, context)
  if (attr.valueType === 'string')
    return stringIngestion(attr, sourceNode, context)
  if (attr.valueType === 'array')
    return arrayIngestion(attr, sourceNode, context)
  return []
}

function standardsIngestion(
  attr: AttributeType,
  sourceNode: Partial<NormalizedIngestionSource>,
  context: IngestionContext,
) {
  const value = sourceNode[attr.key]
  if (!Array.isArray(value)) return []
  const ids = value.reduce<string[]>((acc, v) => {
    if (typeof v === 'string' && !acc.includes(v)) {
      acc.push(v)
    }
    return acc
  }, [])
  if (!ids.length) return []
  return [createSkillsList('Skills', ids)]
}

function stringIngestion(
  attr: AttributeType,
  sourceNode: Partial<NormalizedIngestionSource>,
  context: IngestionContext,
) {
  const title = attr.customTitle || camelToTitle(attr.key)
  const nodeVal = sourceNode[attr.key]
  if (!nodeVal) return []
  const value = capFirst(String(nodeVal))
  return [
    createNoteNode(
      { title, value, studentFacing: !!attr.isStudentFacing },
      context,
    ),
  ]
}

function arrayIngestion(
  attr: AttributeType,
  sourceNode: Partial<NormalizedIngestionSource>,
  context: IngestionContext,
) {
  const listKey = attr.listKey
  if (!listKey) return []
  const sourceArray = sourceNode[attr.key]
  if (!Array.isArray(sourceArray)) return []

  const title = attr.customTitle || camelToTitle(attr.key)
  const listItems = sourceArray.reduce<string[]>((acc, attrObj) => {
    const listItem = attrObj[listKey]
    if (!listItem) return acc
    return [...acc, String(listItem)]
  }, [])
  if (!listItems.length) return []
  const value = createUnorderedList(listItems)
  return [
    createNoteNode(
      { title, value, studentFacing: !!attr.isStudentFacing },
      context,
    ),
  ]
}

function capFirst(s: string) {
  const locale = 'en'
  const div = document.createElement('div')
  div.innerHTML = s
  const walker = document.createTreeWalker(div, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.nodeValue || node.nodeValue.trim() === '')
        return NodeFilter.FILTER_SKIP
      const p = node.parentNode?.nodeName?.toLowerCase()
      if (p === 'script' || p === 'style') return NodeFilter.FILTER_REJECT
      return NodeFilter.FILTER_ACCEPT
    },
  })

  const re = /\p{L}/u

  while (walker.nextNode()) {
    const t = walker.currentNode
    const i = t.nodeValue?.search(re)
    if (i !== -1 && i !== undefined) {
      const s = t.nodeValue!
      t.nodeValue =
        s.slice(0, i) + s[i].toLocaleUpperCase(locale) + s.slice(i + 1)
      break // only the first letter overall
    }
  }
  return div.innerHTML
}
